import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { formatCurrencyInput, sanitizeCurrencyInput } from './dealForm/dealParams';

const numOrNull = (v) => (v === '' ? null : Number(v));

// A currency input that shows the value grouped to two decimals (10,000.00)
// while idle and the raw number while focused — mirroring the Guarantee field.
// Commits a numeric value (or null) on change; the in-progress text is held
// locally so a trailing decimal point survives editing.
const CurrencyInput = ({ value, onChange }) => {
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState('');
  const display = focused ? draft : formatCurrencyInput(value);

  return (
    <input
      type="text"
      inputMode="decimal"
      className="simple-mod__input simple-mod__input--prefix"
      placeholder="0.0"
      value={display}
      onFocus={() => { setDraft(value == null ? '' : String(value)); setFocused(true); }}
      onChange={(e) => {
        const clean = sanitizeCurrencyInput(e.target.value);
        setDraft(clean);
        onChange(numOrNull(clean));
      }}
      onBlur={() => setFocused(false)}
    />
  );
};

CurrencyInput.propTypes = {
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onChange: PropTypes.func.isRequired,
};
CurrencyInput.defaultProps = { value: null };

// Tax-withholding fields styled to match the simple-offer prototype: a pill
// switch gates a 3-column grid (Minimum Threshold $, Withholding %, Due date).
const SimpleTaxWithholdingSection = ({ offer, onChangeField }) => {
  const on = !!offer.has_tax_withholding;

  return (
    <div className="simple-mod" data-testid="modifiers-subsection-tax">
      {on && (
        <div className="simple-mod__grid simple-mod__grid--3">
          <label className="simple-mod__field">
            <span className="simple-mod__label">Minimum Threshold</span>
            <span className="simple-mod__input-wrap">
              <span className="simple-mod__affix simple-mod__affix--prefix">$</span>
              <CurrencyInput
                value={offer.tax_withholding_minimum_threshold ?? null}
                onChange={(v) => onChangeField('tax_withholding_minimum_threshold', v)}
              />
            </span>
          </label>

          <label className="simple-mod__field">
            <span className="simple-mod__label">Withholding</span>
            <span className="simple-mod__input-wrap">
              <input
                type="number"
                step="0.01"
                className="simple-mod__input simple-mod__input--suffix"
                placeholder="0.0"
                value={offer.tax_withholding_percentage ?? ''}
                onChange={(e) => onChangeField('tax_withholding_percentage', numOrNull(e.target.value))}
              />
              <span className="simple-mod__affix simple-mod__affix--suffix"><i className="fas fa-percent" aria-hidden="true" /></span>
            </span>
          </label>

          <label className="simple-mod__field">
            <span className="simple-mod__label">Due date</span>
            <span className="simple-mod__input-wrap">
              <input
                type="number"
                step="1"
                className="simple-mod__input simple-mod__input--suffix-text"
                placeholder="0"
                value={offer.tax_withholding_due_days ?? ''}
                onChange={(e) => onChangeField('tax_withholding_due_days', numOrNull(e.target.value))}
              />
              <span className="simple-mod__affix simple-mod__affix--suffix-text">Days after event</span>
            </span>
          </label>
        </div>
      )}
    </div>
  );
};

SimpleTaxWithholdingSection.propTypes = {
  offer: PropTypes.object.isRequired,
  onChangeField: PropTypes.func.isRequired,
};

export default SimpleTaxWithholdingSection;
